/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import moment, {Moment} from 'moment';
import {getDaysFromToday, getYesterday, today} from '@ion/core/src/utils/DateUtil';
import {DateSelectorValues} from '../../../../types/types';

// `today`/`getYesterday`/`getDaysFromToday` each carry their own reading of
// a range - strings, moments, or a mix - so the one thing every preset is
// resolved through is this, which only ever hands back plain Dates ...
const toDateRange = ({fromDate, toDate}: {fromDate: string | Moment; toDate: string | Moment}) => ({
  from : moment(fromDate).toDate(),
  to   : moment(toDate).toDate()
});

/**
 * Resolves a History preset to the concrete range it stands for, as of now.
 * `Last N Days` reads as N days ending today - the account's own day, not
 * one that closed yesterday - and anything other than a known preset reads
 * as Today.
 *
 * ```typescript
 *
 *   import {getHistoryPresetRange} from '@ion/dls/src/maps/components/timeMode/current/historyRange';
 *   ...
 *
 *   const {from, to} = getHistoryPresetRange(DateSelectorValues.LastSeven);
 *
 * ```
 *
 * @param preset The History preset to resolve.
 * @returns      The `from` and `to` Dates the preset covers.
 */
export const getHistoryPresetRange = (preset: DateSelectorValues): {from: Date; to: Date} => {
  switch (preset) {
    case DateSelectorValues.Yesterday:
      return toDateRange(getYesterday());
    case DateSelectorValues.LastSeven:
      return toDateRange(getDaysFromToday(7, 'subtract', {fromToday: true}));
    case DateSelectorValues.LastThirty:
      return toDateRange(getDaysFromToday(30, 'subtract', {fromToday: true}));
    case DateSelectorValues.Today:
    default:
      return toDateRange(today());
  }
};
